import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, Loader2, Search } from 'lucide-react';
import { adminTicketsAPI, adminLookupAPI } from '../../services/api';

/**
 * Link a CW configuration (asset) to a ticket. Searches within the ticket's
 * company — CW rejects cross-company configuration links.
 */
export default function AdminLinkConfigurationModal({ ticketId, companyId, onClose, onSuccess }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    if (query.trim().length === 0 || query.trim().length >= 2) {
      const timer = setTimeout(async () => {
        setSearching(true);
        try {
          const resp = await adminLookupAPI.configurationSearch(companyId, query.trim());
          setResults(resp.data?.items || []);
        } catch (e) {
          setResults([]);
        } finally {
          setSearching(false);
        }
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [companyId, query]);

  const linkMutation = useMutation({
    mutationFn: (configurationId) => adminTicketsAPI.linkConfiguration(ticketId, configurationId),
    onSuccess: () => {
      onSuccess?.();
      onClose?.();
    },
    onError: (err) =>
      setError(
        err.response?.data?.error || err.response?.data?.details || 'Failed to link configuration'
      ),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Link Configuration
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-neutral-400 hover:text-neutral-700 dark:hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search this company's configurations…"
              className="w-full pl-8 pr-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
            />
            {searching && (
              <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-neutral-400" />
            )}
          </div>

          <div className="max-h-72 overflow-y-auto border border-neutral-200 dark:border-neutral-700 rounded-lg">
            {results.length === 0 && !searching ? (
              <div className="px-3 py-4 text-sm text-neutral-500 dark:text-neutral-400 text-center">
                {query.trim().length === 0
                  ? 'Loading configurations…'
                  : 'No configurations match that name.'}
              </div>
            ) : (
              results.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  disabled={linkMutation.isPending}
                  onClick={() => linkMutation.mutate(c.id)}
                  className="w-full text-left px-3 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 border-b border-neutral-100 dark:border-neutral-700 last:border-b-0 flex items-center justify-between gap-2"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-neutral-900 dark:text-white truncate">
                      {c.name}
                    </div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">
                      {c.type?.name}
                      {c.status?.name && ` • ${c.status.name}`}
                    </div>
                  </div>
                  <span className="text-xs text-primary-600 dark:text-primary-400 flex-shrink-0">
                    Link
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-neutral-200 dark:border-neutral-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
